import React, { Fragment, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import * as PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import IndexLink from 'react-router/lib/IndexLink';
import LinearProgress from 'material-ui/Progress/LinearProgress';

import Icon from '../Icon';
import JoinForm from './components/JoinForm';
import AppointmentSessionLoader from './components/AppointmentSessionLoader';
import AppointmentError from './components/AppointmentError';

import { PAPERWORK_QUERY_PARAMS } from './helpers/queryParams';
import { PAPERWORK_TOKEN } from '../../constants/storageKeys';
import { ROOT } from '../../constants/routes';

import useHandleAutoAuthenticate from './hooks/useHandleAutoAuthenticate';

import brandConfig, { isFlagler } from '../../constants/brandConfig';
import { selectAuthLoggedInStatus } from '../../redux/auth/selectors';
import {
  selectPaperworkGlobalPending,
  selectPaperworkValidating,
} from '../../redux/paperwork/selectors/common';
import {
  selectPaperworkAppointmentSessionError,
  selectPaperworkAppointmentSessionSending,
} from '../../redux/paperwork/selectors/paperwork-appointment-session';
import { selectPaperworkAppointmentDataError } from '../../redux/paperwork/selectors/paperwork-get-appointment-details';

import { paperworkCommonActions } from '../../redux/paperwork/actions/common';
import { manageInsurancesActions } from '../../redux/manage-insurances/actions/manage-insurances-actions';
import { actions as getPaperworkUserDetailsActions } from '../../redux/paperwork/requests/paperwork-get-user-details';
import { actions as getPaperworkAppointmentDetailsActions } from '../../redux/paperwork/requests/paperwork-get-appointment-details';

import styles from './index.styl';
import { IDS } from '../../constants/ids';
import usePaperworkFlowFormSteps from './hooks/usePaperworkFlowFormSteps';
import { useScrollTopByElementId } from '../../hooks/useScrollTopByElementId';

const PaperworkPageLayout = ({ children, query }) => {
  const { formatMessage } = useIntl();
  const { currentStepNumber } = usePaperworkFlowFormSteps();
  const [showContent, setShowContent] = useState(false);

  const showProgress = useSelector(selectPaperworkGlobalPending);

  const isLoggedIn = useSelector(selectAuthLoggedInStatus);

  const LogoContainer = isLoggedIn ? 'div' : IndexLink;
  const logoContainerProps = {
    className: styles.link,
    'aria-label': 'paperwork',
    to: isLoggedIn ? null : ROOT,
  };

  const isAppointmentSessionSending = useSelector(
    selectPaperworkAppointmentSessionSending,
  );

  const validating = useSelector(selectPaperworkValidating);

  const appointmentError = useSelector(selectPaperworkAppointmentDataError);
  const appointmentSessionError = useSelector(
    selectPaperworkAppointmentSessionError,
  );

  useScrollTopByElementId(IDS.PAPERWORK.ROOT, currentStepNumber);

  const visitTitle = formatMessage({ id: 'common.urgentCareVisit' });

  const authToken = get(query, PAPERWORK_QUERY_PARAMS.AUTH_TOKEN);

  const showSessionLoader = useMemo(
    () =>
      (!isLoggedIn && authToken) ||
      (isLoggedIn && validating && !appointmentError),
    [isLoggedIn, validating, appointmentError, authToken],
  );

  const content = useMemo(() => {
    const showJoinForm = !isLoggedIn && !authToken;

    const showChildren =
      isLoggedIn &&
      !authToken &&
      !isAppointmentSessionSending &&
      !appointmentError &&
      !validating;

    const joinFormContent =
      showJoinForm && appointmentSessionError ? (
        <AppointmentError />
      ) : (
        <JoinForm />
      );

    return showSessionLoader || !showContent ? (
      <AppointmentSessionLoader />
    ) : (
      <Fragment>{showChildren ? children : joinFormContent}</Fragment>
    );
  }, [
    appointmentSessionError,
    showSessionLoader,
    showContent,
    isLoggedIn,
    validating,
    authToken,
    isAppointmentSessionSending,
    appointmentError,
    children,
  ]);

  useEffect(() => {
    if (!showSessionLoader) {
      // This fake delay is needed to prevent the content from flickering
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 1000);
      return () => clearTimeout(timer);
      // eslint-disable-next-line no-else-return
    } else {
      setShowContent(false);
    }
  }, [showSessionLoader]);

  return (
    <Fragment>
      <header className={styles.header}>
        <div>
          <LogoContainer {...logoContainerProps}>
            <Icon
              name={brandConfig.logo}
              size={brandConfig.logoSize}
              className={cn({ [styles.icon]: isFlagler })}
            />
          </LogoContainer>
        </div>
        {isLoggedIn && <div className={styles.visitTitle}>{visitTitle}</div>}
      </header>

      <div
        className={cn('g-content', styles.root, {
          [styles.backgroundImage]: !isLoggedIn,
        })}
        id={IDS.PAPERWORK.ROOT}
      >
        {showProgress && <LinearProgress className={styles.progress} />}
        <div
          className={cn(styles.content, {
            [styles.freeze]: showProgress,
          })}
        >
          {content}
        </div>
      </div>
    </Fragment>
  );
};

const Paperwork = ({ children, location }) => {
  const { query } = location;

  const dispatch = useDispatch();

  const isLoggedIn = useSelector(selectAuthLoggedInStatus);

  useHandleAutoAuthenticate(query);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(paperworkCommonActions.validate());
    }
  }, [isLoggedIn]);

  useEffect(() => {
    return () => {
      dispatch(getPaperworkAppointmentDetailsActions.clear());
      dispatch(getPaperworkUserDetailsActions.clear());
      dispatch(manageInsurancesActions.clearManageInsuranceRelatedData());
      window.storage.removeItem(PAPERWORK_TOKEN);
    };
  }, []);

  return <PaperworkPageLayout query={query}>{children}</PaperworkPageLayout>;
};

PaperworkPageLayout.propTypes = {
  children: PropTypes.node,
  query: PropTypes.object,
};

Paperwork.propTypes = {
  children: PropTypes.node,
  location: PropTypes.object,
};

export default Paperwork;



// WEBPACK FOOTER //
// ./src/components/Paperwork/index.jsx